import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as ActuacionContractualActions from './actuacion-contractual.actions';
import * as ActuacionContractualSelectors from './actuacion-contractual.selectors';
import { ActuacionContractualService } from '../services/actuacion-contractual.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { LoggerService } from '../../../core/services/logger.service';

@Injectable()
export class ActuacionContractualEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private actuacionContractualService = inject(ActuacionContractualService);
  private notificationService = inject(NotificationService);
  private logger = inject(LoggerService);

  loadActuacionesContractuales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActuacionContractualActions.loadActuacionesContractuales),
      withLatestFrom(
        this.store.select(ActuacionContractualSelectors.selectAllActuacionesContractuales)
      ),
      mergeMap(([{ force }, current]) => {
        if (!force && current && current.length > 0) {
          // Already have data; skip network call
          return of(
            ActuacionContractualActions.loadActuacionesContractualesSuccess({
              actuacionesContractuales: current
            })
          );
        }

        return this.actuacionContractualService.getActuacionesContractuales().pipe(
          map(actuacionesContractuales =>
            ActuacionContractualActions.loadActuacionesContractualesSuccess({
              actuacionesContractuales,
              loadedAt: Date.now()
            })
          ),
          catchError(error => {
            this.logger.error('Error loading actuaciones contractuales', error);
            this.notificationService.showError('Error al cargar las actuaciones contractuales.');
            return of(
              ActuacionContractualActions.loadActuacionesContractualesFailure({
                error: error.message
              })
            );
          })
        );
      })
    )
  );

  createActuacionContractual$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActuacionContractualActions.createActuacionContractual),
      mergeMap(({ actuacionContractual }) =>
        this.actuacionContractualService.createActuacionContractual(actuacionContractual).pipe(
          mergeMap(createdActuacionContractual => {
            this.notificationService.showSuccess('Actuación contractual creada exitosamente.');
            return of(
              ActuacionContractualActions.createActuacionContractualSuccess({
                actuacionContractual: createdActuacionContractual
              }),
              ActuacionContractualActions.loadActuacionesContractuales({ force: true })
            );
          }),
          catchError(error => {
            this.logger.error('Error creating actuacion contractual', error);
            this.notificationService.showError('Error al crear la actuación contractual.');
            return of(
              ActuacionContractualActions.createActuacionContractualFailure({
                error: error.message
              })
            );
          })
        )
      )
    )
  );

  updateActuacionContractual$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActuacionContractualActions.updateActuacionContractual),
      withLatestFrom(
        this.store.select(ActuacionContractualSelectors.selectAllActuacionesContractuales)
      ),
      mergeMap(([{ id, actuacionContractual }, actuacionesContractuales]) => {
        const currentActuacion = actuacionesContractuales.find(ac => ac.actuacionId === id);
        return this.actuacionContractualService
          .updateActuacionContractual(id, actuacionContractual)
          .pipe(
            map(() => {
              this.notificationService.showSuccess(
                'Actuación contractual actualizada exitosamente.'
              );
              return ActuacionContractualActions.updateActuacionContractualSuccess({
                actuacionContractual: {
                  ...currentActuacion,
                  actuacionId: id,
                  tipo: actuacionContractual.Tipo
                }
              });
            }),
            catchError(error => {
              this.logger.error('Error updating actuacion contractual', error);
              this.notificationService.showError('Error al actualizar la actuación contractual.');
              return of(
                ActuacionContractualActions.updateActuacionContractualFailure({
                  error: error.message
                })
              );
            })
          );
      })
    )
  );

  toggleActuacionContractualStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActuacionContractualActions.toggleActuacionContractualStatus),
      withLatestFrom(
        this.store.select(ActuacionContractualSelectors.selectAllActuacionesContractuales)
      ),
      mergeMap(([{ id, isActive }]) => {
        return this.actuacionContractualService.toggleActuacionContractualStatus(id, isActive).pipe(
          map(() => {
            this.notificationService.showSuccess(
              'Estado de actuación contractual cambiado exitosamente.'
            );
            return ActuacionContractualActions.toggleActuacionContractualStatusSuccess({
              id,
              isActive
            });
          }),
          catchError(error => {
            this.logger.error('Error toggling actuacion contractual status', error);
            this.notificationService.showError(
              'Error al cambiar el estado de la actuación contractual.'
            );
            return of(
              ActuacionContractualActions.toggleActuacionContractualStatusFailure({
                error: error.message
              })
            );
          })
        );
      })
    )
  );

  searchActuacionesContractuales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActuacionContractualActions.searchActuacionesContractuales),
      mergeMap(({ query }) => {
        if (!query.trim()) {
          return of(ActuacionContractualActions.clearActuacionContractualSearch());
        }

        return this.actuacionContractualService.getActuacionesContractuales().pipe(
          map(actuacionesContractuales => {
            const searchResults = actuacionesContractuales.filter(ac =>
              ac.tipo.toLowerCase().includes(query.toLowerCase())
            );
            return ActuacionContractualActions.searchActuacionesContractualesSuccess({
              searchResults
            });
          }),
          catchError(error => {
            this.logger.error('Error searching actuaciones contractuales', error);
            return of(
              ActuacionContractualActions.searchActuacionesContractualesFailure({
                error: error.message
              })
            );
          })
        );
      })
    )
  );
}
